import { oc } from '@orpc/contract';
import { ROUTED_TO, VOCAB_NAME_MAX, WAITING_ON } from '@lsc/domain';
import { z } from 'zod';
/** Category and product are checked against the live pickers (support.ticket_vocab) in createTicket, not here. */
const vocabValue = z.string().trim().min(1).max(VOCAB_NAME_MAX);
export const ticketCreateInput = z.object({
    accountId: z.uuid().optional(),
    /** A recognised caller (pending draft) the rep picked; the ticket hangs on it until cleanup links it. */
    draftId: z.uuid().optional(),
    /** As typed; parsed server-side. Kept verbatim on the ticket either way. */
    phone: z.string().trim().max(40).optional(),
    callerCompany: z.string().trim().max(200).optional(),
    callerName: z.string().trim().max(200).optional(),
    callerEmail: z.email().optional(),
    /** The contact-corruption guard: name or email reach the contact only when the rep changed the prefill. */
    contactChanged: z.object({ name: z.boolean(), email: z.boolean() }).default({ name: false, email: false }),
    subject: z.string().trim().min(3).max(140),
    category: vocabValue.optional(),
    product: vocabValue.optional(),
    notes: z.string().max(5000).optional(),
    routedTo: z.enum(ROUTED_TO).optional(),
    waitingOn: z.enum(WAITING_ON).optional(),
    assignToSelf: z.boolean().default(true),
    /** Hand the ticket to another active staff member at creation (the rep who answered the call, logged by someone else, 2026-11-03). Wins over assignToSelf. */
    assignTo: z.uuid().optional(),
    /** Fast close: resolved in the same write with close reason "Resolved". */
    resolveNow: z.object({ resolution: z.string().trim().min(1).max(2000) }).optional(),
    source: z.enum(['phone', 'internal', 'slack_bot']).default('phone'),
    /** The Dialpad root call the ticket was logged from (a pop card); decimal string. */
    rootCallId: z.string().regex(/^\d{1,20}$/).optional(),
    origin: z.object({ slackChannel: z.string(), slackTs: z.string(), permalink: z.string().optional() }).optional(),
});
export const ticketCreateOutput = z.object({ ticketId: z.uuid(), ticketNumber: z.string() });
export const ticketCreate = oc.route({ method: 'POST', path: '/tickets/create', summary: 'Log a ticket' }).input(ticketCreateInput).output(ticketCreateOutput);
const ticketRef = { ticketId: z.uuid() };
const closeFields = { resolution: z.string().max(2000), closeReason: z.string().max(60), rootCause: z.string().max(60).optional() };
/**
 * Claim takes an unheld ticket. `takeOverFrom` (the current holder's profile id) takes a held one,
 * logged as a hand-off (Jeff, 2026-10-22: anyone active can act on a ticket, we log it). Either
 * way a stale view is told who has it rather than surprised.
 */
/** Edit or remove your own note (Carlyrose, 2026-10-22; Jeff: their own only). The history keeps the earlier text. */
export const ticketNoteEditInput = z.object({ ...ticketRef, eventId: z.number().int(), text: z.string().trim().min(1).max(5000) });
export const ticketNoteDeleteInput = z.object({ ...ticketRef, eventId: z.number().int() });
/** A board stage by its label; `scheduledFor` (YYYY-MM-DD) only with Scheduled. The rules live in `planStageMove`. */
export const ticketMoveStageInput = z.object({ ...ticketRef, stage: z.string().trim().min(1).max(60), scheduledFor: z.string().max(10).nullable().optional() });
export const ticketClaimInput = z.object({ ...ticketRef, takeOverFrom: z.uuid().optional() });
export const ticketRouteInput = z.object({ ...ticketRef, routedTo: z.enum(ROUTED_TO).nullable().optional(), waitingOn: z.enum(WAITING_ON).nullable().optional() });
export const ticketSetStatusInput = z.object({ ...ticketRef, status: z.enum(['open', 'in_progress', 'waiting_on_merchant', 'stuck']) });
export const ticketCloseInput = z.object({ ...ticketRef, ...closeFields });
export const ticketReopenInput = z.object({ ...ticketRef, status: z.enum(['open', 'in_progress']) });
/** `mentions`: teammates picked from the @ list (emails); each still tagged in the text gets a Slack DM. */
/** `kind: 'shift_note'`: the one line a rep leaves for whoever picks the ticket up when going off duty. */
export const ticketCommentInput = z
    .object({
    ...ticketRef,
    text: z.string().trim().min(1).max(5000),
    mentions: z.array(z.object({ email: z.string() })).max(20).optional(),
    kind: z.enum(['note', 'shift_note']).optional(),
    /** Uploads from POST /tickets/:id/files, linked to this note in its transaction. */
    attachmentIds: z.array(z.uuid()).max(10).optional(),
})
    .refine((v) => v.kind !== 'shift_note' || (v.text.length <= 200 && !/[\r\n]/.test(v.text)), { message: 'A shift note is one line of up to 200 characters.', path: ['text'] });
//# sourceMappingURL=ticket-write.js.map